import apiClient from './apiClient';

export type AnnouncementCategory = 'deploy' | 'patch_critica' | 'manutenzione';
export type AnnouncementStatus = 'programmato' | 'in_corso' | 'completato';

export interface Announcement {
  id: number;
  category: AnnouncementCategory;
  status: AnnouncementStatus;
  title: string;
  startAt: string;
  downtimeEstimate: string | null;
  userImpact: string;
  detailsMd: string | null;
  softwareVersion: string | null;
  isPublished: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  dismissedAt: string | null;
}

export interface CurrentAnnouncement extends Announcement {
  shouldNudge: boolean;
  nudgeAfterMinutes: number;
}

export interface AnnouncementWritePayload {
  category: AnnouncementCategory;
  status: AnnouncementStatus;
  title: string;
  start_at: string;
  downtime_estimate?: string | null;
  user_impact: string;
  details_md?: string | null;
  software_version?: string | null;
}

export interface FieldError {
  error: string;
  field: string;
  message: string;
}

// ── Import / Export ───────────────────────────────────────────────────────
// The exported file is a portable JSON the admin moves between the per-comune
// frontends (no cross-deployment network call). Export dumps the full record
// for readability; import consumes only the content fields.

export const ANNOUNCEMENT_FILE_TYPE = 'home_announcement';
export const ANNOUNCEMENT_FILE_VERSION = 1;

export interface AnnouncementFile {
  _type: typeof ANNOUNCEMENT_FILE_TYPE;
  _version: number;
  announcement: Announcement;
}

/** Build a download filename like `avviso-manutenzione-2026-05-28.json`. */
function exportFilename(a: Announcement): string {
  const datePart = a.startAt ? a.startAt.slice(0, 10) : 'senza-data';
  return `avviso-${a.category}-${datePart}.json`;
}

/** Pull the snake_case write payload out of a camelCase announcement,
 *  keeping only content fields (ignoring id/audit/isPublished/dismissedAt). */
function toWritePayload(a: Announcement): AnnouncementWritePayload {
  return {
    category: a.category,
    status: a.status,
    title: a.title,
    start_at: a.startAt,
    downtime_estimate: a.downtimeEstimate,
    user_impact: a.userImpact,
    details_md: a.detailsMd,
    software_version: a.softwareVersion,
  };
}

class AnnouncementService {
  async getActive(): Promise<Announcement | null> {
    const res = await apiClient.get<Announcement>('/api/announcements/active', {
      validateStatus: (s) => s === 200 || s === 204,
    });
    if (res.status === 204) return null;
    return res.data;
  }

  async getCurrent(): Promise<CurrentAnnouncement | null> {
    const res = await apiClient.get<CurrentAnnouncement>(
      '/api/announcements/current',
      { validateStatus: (s) => s === 200 || s === 204 },
    );
    if (res.status === 204) return null;
    return res.data;
  }

  async list(): Promise<Announcement[]> {
    const res = await apiClient.get<Announcement[]>('/api/announcements');
    return res.data;
  }

  async create(payload: AnnouncementWritePayload): Promise<Announcement> {
    const res = await apiClient.post<Announcement>(
      '/api/announcements',
      payload,
    );
    return res.data;
  }

  async update(
    id: number,
    payload: Partial<AnnouncementWritePayload>,
  ): Promise<Announcement> {
    const res = await apiClient.patch<Announcement>(
      `/api/announcements/${id}`,
      payload,
    );
    return res.data;
  }

  async publish(id: number): Promise<Announcement> {
    const res = await apiClient.post<Announcement>(
      `/api/announcements/${id}/publish`,
    );
    return res.data;
  }

  async archive(id: number): Promise<Announcement> {
    const res = await apiClient.post<Announcement>(
      `/api/announcements/${id}/archive`,
    );
    return res.data;
  }

  async remove(id: number): Promise<void> {
    await apiClient.delete(`/api/announcements/${id}`);
  }

  /** Serialize an announcement into a typed JSON file and trigger a download. */
  exportToFile(a: Announcement): void {
    const payload: AnnouncementFile = {
      _type: ANNOUNCEMENT_FILE_TYPE,
      _version: ANNOUNCEMENT_FILE_VERSION,
      announcement: a,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportFilename(a);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /** Read an exported file, validate its header/shape, and return the
   *  content-only write payload. Throws an Error (Italian message) when the
   *  file is not a valid exported announcement. */
  async parseImportFile(file: File): Promise<AnnouncementWritePayload> {
    let parsed: any;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      throw new Error('File non valido: non è un JSON leggibile.');
    }
    if (!parsed || parsed._type !== ANNOUNCEMENT_FILE_TYPE) {
      throw new Error('File non valido: non è un avviso esportato.');
    }
    if (parsed._version !== ANNOUNCEMENT_FILE_VERSION) {
      throw new Error(
        `Versione file non supportata (${parsed._version}). Attesa: ${ANNOUNCEMENT_FILE_VERSION}.`,
      );
    }
    const a = parsed.announcement;
    if (!a || typeof a !== 'object') {
      throw new Error('File non valido: campo "announcement" mancante.');
    }
    return toWritePayload(a as Announcement);
  }
}

export const announcementService = new AnnouncementService();
